import React, { useState, useEffect, useRef } from "react";
import Editor, { useMonaco, loader } from "@monaco-editor/react";
import Sidebar from "./components/Sidebar";
import KeywordsPanel from "./components/KeywordsPanel";
import { getCompletionItems } from "./utils/autocomplete";
import { DiagnosticsEngine } from "./utils/DiagnosticsEngine";

// Aggressive suppression of Monaco's internal cancellation errors
const suppressMonacoErrors = (e) => {
  const reason = e.reason || e.error;
  if (!reason) return;
  const isCancellation = reason.name === 'Canceled' || reason.message === 'Canceled' || reason.type === 'cancelation' || (typeof reason === 'object' && Object.keys(reason).length === 0);
  if (isCancellation) {
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    if (e.stopPropagation) e.stopPropagation();
    e.preventDefault();
    return true;
  }
};

window.addEventListener('unhandledrejection', suppressMonacoErrors, { capture: true });
window.addEventListener('error', (e) => {
  if (suppressMonacoErrors(e)) return;
  if (e.target && (e.target.tagName === 'LINK' || e.target.tagName === 'SCRIPT')) {
    window.location.reload();
  }
}, { capture: true });

export default function App() {
  const [version, setVersion] = useState("MA2");
  const [fileName, setFileName] = useState("main.xml");
  const [code, setCode] = useState("");
  const [keywords, setKeywords] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [mobileMenu, setMobileMenu] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const [isMonacoConfigured, setIsMonacoConfigured] = useState(false);

  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const completionProviderRef = useRef(null);
  const versionRef = useRef("MA2");

  const isMA3 = version === "MA3";

  // Initial Load
  useEffect(() => {
    loadKeywords(true);
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync version
  useEffect(() => {
    versionRef.current = version;
    setSelectedKeyword(null);
    setKeywordFilter("");
    loadKeywords(true);

    const themeColor = version === "MA3" ? "#FFFFFF" : "#1E1E1E";
    const meta = document.querySelector('meta[name="theme-color"]') || document.createElement("meta");
    meta.name = "theme-color";
    meta.content = themeColor;
    if (!meta.parentNode) document.head.appendChild(meta);
  }, [version]);

  const loadKeywords = (cacheBust = false) => {
    const file = version === "MA3" ? "/ma3_keywords.json" : "/ma2_keywords.json";
    const url = cacheBust ? `${file}?t=${Date.now()}` : file;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data && data.keywords) setKeywords(data);
        else setKeywords({ keywords: {} });
      })
      .catch(() => setKeywords({ keywords: {} }));
  };

  const handleEditorBeforeMount = (monaco) => {
    const langId = "ma-macro";

    // Register Language
    if (!monaco.languages.getLanguages().some(l => l.id === langId)) {
      monaco.languages.register({ id: langId });
    }

    // Register Themes EARLY
    monaco.editor.defineTheme("ma-theme-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [
        { token: "keyword.function", foreground: "facc15", fontStyle: "bold" },
        { token: "keyword.object", foreground: "60a5fa" },
        { token: "keyword.operator", foreground: "f87171" },
        { token: "keyword.helper", foreground: "a78bfa" },
        { token: "string", foreground: "4ade80" },
        { token: "number", foreground: "fb923c" },
        { token: "variable", foreground: "34d399" },
        { token: "annotation", foreground: "94a3b8" },
      ],
      colors: {
        "editor.background": "#1E1E1E",
        "editor.lineHighlightBackground": "#2A2A2A",
      }
    });

    monaco.editor.defineTheme("ma-theme-light", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "keyword.function", foreground: "2563eb", fontStyle: "bold" },
        { token: "keyword.object", foreground: "7c3aed" },
        { token: "keyword.operator", foreground: "dc2626" },
        { token: "keyword.helper", foreground: "059669" },
        { token: "string", foreground: "16a34a" },
        { token: "number", foreground: "ea580c" },
        { token: "variable", foreground: "0891b2" },
        { token: "annotation", foreground: "64748b" },
      ],
      colors: {
        "editor.background": "#FFFFFF",
        "editor.lineHighlightBackground": "#F3F4F6",
      }
    });

    setIsMonacoConfigured(true);
  };

  const monacoInstance = useMonaco();

  // Dynamic Tokenizer & Autocomplete Setup
  useEffect(() => {
    if (!monacoInstance || !keywords || !keywords.keywords) return;

    const langId = "ma-macro";
    const kMap = keywords.keywords;
    const commonObjects = ["Sequence", "Seq", "Cue", "Group", "Preset", "Fixture", "Fix", "Channel", "Chan", "Macro", "Page", "Exec", "Executor", "World", "Plugin", "Layout", "View", "User", "Variable", "Var"];
    const commonHelpers = ["At", "If", "Except", "And", "Through", "Thru", "Wait", "With", "In", "On", "Off"];

    // Categorize keywords from data
    const rawKwList = Object.entries(kMap).filter(([_, d]) => d.semantic_type === 'keyword' || d.semantic_type === 'function').map(([n]) => n);
    const rawObjList = Object.entries(kMap).filter(([_, d]) => d.semantic_type === 'object').map(([n]) => n);
    const kwList = rawKwList.filter(k => !commonObjects.includes(k) && !commonHelpers.includes(k));
    const objList = [...rawObjList, ...commonObjects];
    const helpList = commonHelpers;

    // Set Tokenizer
    monacoInstance.languages.setMonarchTokensProvider(langId, {
      ignoreCase: true,
      tokenizer: {
        root: [
          [/[0-9.]+/, "number"],
          [/"([^"\\]|\\.)*"/, "string"],
          [/\$[a-zA-Z_$][\w$]*/, "variable"],
          [/\[.*\]/, "annotation"], // Conditional blocks
          [/[\/\+\-\*\=\@\(\)]/, "operator"],
          [/[a-zA-Z_$][\w$]*/, {
            cases: {
              "@kwList": "keyword.function",
              "@objList": "keyword.object",
              "@helpList": "keyword.helper",
              "@default": "identifier"
            }
          }],
        ],
      },
      kwList: kwList.length > 0 ? kwList : ["__empty__"],
      objList: objList.length > 0 ? objList : ["__empty__"],
      helpList: helpList.length > 0 ? helpList : ["__empty__"]
    });

    if (completionProviderRef.current) completionProviderRef.current.dispose();
    completionProviderRef.current = monacoInstance.languages.registerCompletionItemProvider(langId, {
      triggerCharacters: [" ", "/", "$", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "@", ".", "+", "-"],
      provideCompletionItems: (model, position) => {
        return getCompletionItems(model, position, keywords, versionRef.current, monacoInstance);
      }
    });

    return () => {
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
        completionProviderRef.current = null;
      }
    };
  }, [monacoInstance, keywords, version]);

  // Diagnostics
  useEffect(() => {
    if (!monacoInstance || !editorRef.current || !keywords || !keywords.keywords) return;
    const model = editorRef.current.getModel();
    if (!model) return;

    const engine = new DiagnosticsEngine(keywords.keywords, version);
    const lines = code.split('\n');
    let markers = [];
    lines.forEach((line, idx) => {
      markers = [...markers, ...engine.validateLine(line, idx + 1)];
    });
    monacoInstance.editor.setModelMarkers(model, "ma-diagnostics", markers);
  }, [code, version, keywords, monacoInstance]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (isMod && e.key === 'o') {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [code, fileName, version]);

  const insertKeyword = (kw) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const select = editor.getSelection();
      editor.executeEdits("insert", [{ range: select, text: kw, forceMoveMarkers: true }]);
      editor.focus();
    }
  };

  const handleSave = () => {
    const s = code.split("\n").filter(l => l.trim());
    const name = fileName.replace(/\.xml$/i, "");
    const guid = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join(' ');
    const esc = (v) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

    let xml = "";
    if (version === "MA2") {
      const l = s.map((c, i) => `<Macroline index="${i}"><text>${esc(c)}</text></Macroline>`).join("\n");
      xml = `<?xml version="1.0" encoding="utf-8"?><MA xmlns="http://schemas.malighting.de/grandma2/xml/MA" major_vers="3" minor_vers="9"><Macro index="1" name="${esc(name)}">${l}</Macro></MA>`;
    } else {
      const l = s.map(c => `<MacroLine Command="${esc(c)}" />`).join("\n");
      xml = `<?xml version="1.0" encoding="UTF-8"?><GMA3 DataVersion="2.0.2.0"><Macro Name="${esc(name)}" Guid="${guid.toUpperCase()}">${l}</Macro></GMA3>`;
    }

    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const txt = ev.target.result;
      try {
        const parser = new DOMParser();
        const xml = parser.parseFromString(txt, "application/xml");

        const macroElements = xml.querySelectorAll("Macro");
        if (macroElements.length === 0) {
          alert("No macros found in this XML file.");
          return;
        }

        const isMA3File = xml.querySelector("GMA3") !== null;
        setVersion(isMA3File ? "MA3" : "MA2");
        setFileName(file.name);

        let lines = [];
        if (isMA3File) {
          lines = Array.from(xml.querySelectorAll("MacroLine"))
            .filter(el => el.getAttribute("Disabled") !== "true" && el.getAttribute("disabled") !== "true")
            .map(el => el.getAttribute("Command") || el.getAttribute("command"))
            .filter(Boolean);
        } else {
          lines = Array.from(xml.querySelectorAll("Macroline"))
            .filter(el => el.getAttribute("disabled") !== "true" && el.getAttribute("Disabled") !== "true")
            .map(el => {
              const textNode = el.querySelector("text");
              return textNode ? textNode.textContent : "";
            })
            .filter(s => s !== null);
        }

        setCode(lines.join("\n"));

      } catch (err) {
        console.error("Error parsing macro XML:", err);
        alert("Failed to parse the XML file.");
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const [libraryHeight, setLibraryHeight] = useState(40); // Percentage
  const [isResizing, setIsResizing] = useState(false);

  // Resizing logic
  const handleStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!isResizing) return;

      // Support both Mouse and Touch
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const height = ((window.innerHeight - clientY) / window.innerHeight) * 100;

      if (height > 5 && height < 85) {
        setLibraryHeight(height);
      }
    };

    const handleEnd = () => setIsResizing(false);

    if (isResizing) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isResizing]);

  return (
    <div className={`flex flex-col xl-custom:flex-row h-screen font-sans overflow-hidden ${isMA3 ? "bg-white text-gray-900" : "bg-ma2-bg text-white"}`}>

      {/* Mobile Sidebar Backdrop */}
      {mobileMenu === "sidebar" && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] xl-custom:hidden transition-opacity"
          onClick={() => setMobileMenu(null)}
        />
      )}

      <div className={`fixed inset-y-0 left-0 z-[70] transform transition-transform duration-300 xl-custom:relative xl-custom:translate-x-0 w-80
        ${mobileMenu === "sidebar" ? "translate-x-0" : "-translate-x-full xl-custom:translate-x-0"}`}>
        <Sidebar version={version} setVersion={setVersion} handleOpenClick={() => fileInputRef.current.click()} handleSave={handleSave} isMA3={isMA3}
          onKeywordsUpdate={() => loadKeywords(true)} onClose={() => setMobileMenu(null)} onOpenLibrary={() => setMobileMenu('library')} />
      </div>

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className={`h-14 flex items-center px-4 justify-between border-b ${isMA3 ? "bg-white border-gray-200" : "bg-[#1E1E1E] border-gray-800"}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenu('sidebar')}
              className={`xl-custom:hidden p-2 -ml-2 rounded-lg transition-colors ${isMA3 ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 hover:bg-white/10'
                }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-2 text-sm font-bold opacity-60 uppercase tracking-widest">
              <span className="hidden sm:inline">Macro Editor</span>
              <span className="opacity-30 hidden sm:inline">|</span>
              <span>{fileName.toUpperCase()}</span>
            </div>
          </div>
          <span className="text-sm font-medium opacity-40">{code.split('\n').length} lines</span>
        </div>

        <div className="flex-1 relative overflow-hidden bg-[#1E1E1E]">
          <Editor
            height="100%"
            language="ma-macro"
            theme={isMA3 ? "ma-theme-light" : "ma-theme-dark"}
            value={code}
            onChange={setCode}
            beforeMount={handleEditorBeforeMount}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              // Add Keybindings to Editor
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => handleSave());
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyO, () => fileInputRef.current?.click());
            }}
            options={{
              padding: { top: 20, bottom: 20 },
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 14,
              lineHeight: 24,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              smoothScrolling: true,
              cursorBlinking: "smooth",
              renderLineHighlight: "all",
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              automaticLayout: true,
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
              }
            }}
          />
        </div>

        {/* Resizer Handle - Balanced for Touch & Desktop */}
        <div
          onMouseDown={handleStart}
          onTouchStart={handleStart}
          className={`hidden xl-custom:flex h-2 cursor-ns-resize z-50 items-center justify-center select-none outline-none
            ${isResizing
              ? (isMA3 ? "bg-blue-500/20" : "bg-yellow-500/20")
              : (isMA3 ? "bg-gray-100 hover:bg-blue-500/10" : "bg-white/5 hover:bg-yellow-500/10")
            }
          `}
        >
          <div className={`w-12 h-0.5 rounded-full 
            ${isResizing
              ? (isMA3 ? "bg-blue-500" : "bg-yellow-500")
              : "bg-gray-500/30"
            }`}
          />
        </div>

        {/* Keywords Panel (Bottom Half or Full Screen Mobile) */}
        <div
          style={{ height: isMobile ? '100%' : `${libraryHeight}%` }}
          className={`
            fixed xl-custom:relative inset-0 xl-custom:inset-auto z-[80] xl-custom:z-10
            flex flex-col border-t border-gray-500/10 xl-custom:shadow-inner
            transition-transform duration-300 transform xl-custom:transform-none
            ${mobileMenu === 'library' ? 'translate-y-0' : 'translate-y-full xl-custom:translate-y-0'}
            ${isMA3 ? 'bg-white' : 'bg-[#151515]'}
          `}
        >
          {/* Mobile Library Close Button */}
          <div className={`xl-custom:hidden flex flex-shrink-0 items-center justify-between p-4 border-b ${isMA3 ? "border-gray-200" : "border-gray-800"}`}>
            <h2 className={`text-sm font-bold tracking-wider uppercase opacity-70 ${isMA3 ? 'text-gray-900' : 'text-gray-100'}`}>Library</h2>
            <button
              onClick={() => setMobileMenu(null)}
              className="p-2 rounded-lg hover:bg-black/10 transition-colors"
            >
              <svg className={`w-6 h-6 ${isMA3 ? 'text-gray-900' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={`flex-1 min-h-0 ${isResizing ? "pointer-events-none select-none" : ""}`}>
            <KeywordsPanel
              keywordsLines={Object.entries(keywords?.keywords || {})}
              keywords={keywords?.keywords || {}}
              keywordFilter={keywordFilter} setKeywordFilter={setKeywordFilter}
              selectedKeyword={selectedKeyword} setSelectedKeyword={setSelectedKeyword}
              onInsert={insertKeyword} isMA3={isMA3}
            />
          </div>
        </div>
      </div>
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".xml,application/xml,text/xml" />
    </div>
  );
}
